import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { format } from 'date-fns';
import LoadingSpinner from '@medical/shared/components/LoadingSpinner';
import toast from 'react-hot-toast';
import DistressThermometer from '../components/DistressThermometer';
import { useOfflineSync } from '../contexts/OfflineSyncContext';
import { useTranslation } from 'react-i18next';
import { TranslatedText, TranslatedTextArray } from '@medical/shared/components/TranslatedText';

const DistressContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;

  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 1.125rem;
  margin: 0;
`;

const ControlsSection = styled.div`
  background: white;
  border-radius: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterLabel = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: white;
  font-size: 0.875rem;
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const DateInput = styled.input`
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  background: white;
  font-size: 0.875rem;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const RefreshButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #2563eb;
  }
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DistressList = styled.div`
  background: white;
  border-radius: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const DistressHeader = styled.div`
  background: #f8fafc;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DistressTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const DistressItem = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  transition: background 0.2s ease;

  &:hover {
    background: #f9fafb;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const DistressItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  gap: 1rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const DistressScoreContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const DistressScore = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DistressBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 700;
  background: ${props => {
    if (props.score >= 8) return '#fee2e2';
    if (props.score >= 4) return '#fef3c7';
    if (props.score >= 1) return '#dbeafe';
    return '#d1fae5';
  }};
  color: ${props => {
    if (props.score >= 8) return '#991b1b';
    if (props.score >= 4) return '#92400e';
    if (props.score >= 1) return '#1e40af';
    return '#065f46';
  }};
  border: 2px solid ${props => {
    if (props.score >= 8) return '#fecaca';
    if (props.score >= 4) return '#fde68a';
    if (props.score >= 1) return '#bfdbfe';
    return '#a7f3d0';
  }};
`;

const DistressTime = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
`;

const DistressDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const DistressSources = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const SourceTag = styled.span`
  background: #f3f4f6;
  color: #374151;
  padding: 0.25rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
`;

const DistressNotes = styled.div`
  background: #f9fafb;
  padding: 1rem;
  border-radius: 0.5rem;
  border-left: 3px solid #3b82f6;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #374151;
  line-height: 1.6;
`;

const HighDistressAlert = styled.div`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 1rem;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
`;

const StatusGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.85rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  border: 1px solid
    ${props =>
      props.status === 'online'
        ? '#10b981'
        : props.status === 'syncing'
        ? '#3b82f6'
        : '#f59e0b'};
  background:
    ${props =>
      props.status === 'online'
        ? 'rgba(16, 185, 129, 0.12)'
        : props.status === 'syncing'
        ? 'rgba(59, 130, 246, 0.12)'
        : 'rgba(245, 158, 11, 0.12)'};
  color:
    ${props =>
      props.status === 'online'
        ? '#047857'
        : props.status === 'syncing'
        ? '#1d4ed8'
        : '#92400e'};
`;

const PendingBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: #fef3c7;
  border: 1px solid #facc15;
  color: #92400e;
  font-size: 0.75rem;
  font-weight: 600;
`;

const SyncButton = styled.button`
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #1d4ed8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PendingLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  background: #fde68a;
  color: #92400e;
  font-size: 0.7rem;
  font-weight: 600;
`;

// Emoji mapping for problem list items
const problemEmojis = {
  // Practical Problems
  transportation: '🚗',
  housing: '🏠',
  insuranceFinancial: '💰',
  workSchool: '💼',
  // Family Problems
  dealingWithPartner: '👫',
  dealingWithChildren: '👨‍👩‍👧',
  familyHealthIssues: '👪',
  // Emotional Problems
  depression: '😔',
  fears: '😨',
  nervousness: '😰',
  sadness: '😢',
  worry: '😟',
  lossOfInterest: '😑',
  // Spiritual/Religious Concerns
  spiritualReligiousConcerns: '🙏',
  // Physical Problems
  fatigue: '😴',
  pain: '🤕',
  sleepProblems: '😴',
  eatingProblems: '🍽️',
  bathingDressing: '🛁',
  breathingProblems: '😮‍💨',
  nausea: '🤢',
  memoryConcentration: '🧠',
  mouthSores: '👄',
  skinProblems: '🤲',
  tinglingHandsFeet: '🦶',
  otherPhysicalProblems: '🏥'
};

const Distress = () => {
  const { t } = useTranslation();
  const [distressLogs, setDistressLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    minScore: '',
    startDate: '',
    endDate: ''
  });

  const {
    isOnline,
    syncing,
    pendingDistressLogs,
    triggerSync,
    lastSync
  } = useOfflineSync();

  const fetchDistressLogs = useCallback(async () => {
    if (!isOnline) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        setError(t('distress.error.login'));
        return;
      }

      const params = {};
      
      if (filters.minScore) {
        params.minScore = parseInt(filters.minScore);
      }
      if (filters.startDate) {
        params.startDate = new Date(filters.startDate).toISOString();
      }
      if (filters.endDate) {
        const endDate = new Date(filters.endDate);
        endDate.setHours(23, 59, 59, 999);
        params.endDate = endDate.toISOString();
      }

      const response = await axios.get('/api/distress-logs', {
        params: {
          ...params,
          limit: 100
        },
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        const logs = response.data.distressLogs || [];
        setDistressLogs(logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
      } else {
        throw new Error(response.data.message || 'Failed to fetch distress logs');
      }
    } catch (err) {
      console.error('Error fetching distress logs:', err);
      setError(err.response?.data?.message || t('distress.error.message'));
      toast.error(t('distress.error.message'));
    } finally {
      setLoading(false);
    }
  }, [filters, isOnline, t]);

  useEffect(() => {
    fetchDistressLogs();
  }, [fetchDistressLogs, lastSync]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const combinedDistressLogs = useMemo(() => {
    const pending = (pendingDistressLogs || []).map(entry => ({
      ...entry,
      id: entry.localId,
      isPending: true,
      timestamp: entry.timestamp || entry.createdAt
    }));

    const live = (distressLogs || []).map(log => ({
      ...log,
      id: log._id || log.id,
      isPending: false
    }));

    let combined = [...pending, ...live];

    // Apply minimum score filter if selected in dropdown
    if (filters.minScore) {
      const minScoreValue = parseInt(filters.minScore);
      combined = combined.filter(log => {
        const score = log.distressScore;
        return typeof score === 'number' && score >= minScoreValue;
      });
    }

    return combined.sort(
      (a, b) =>
        new Date(b.timestamp || b.createdAt || 0).getTime() -
        new Date(a.timestamp || a.createdAt || 0).getTime()
    );
  }, [distressLogs, pendingDistressLogs, filters.minScore]);

  const getDistressLabel = (score) => {
    if (score >= 8) return t('distress.labels.extreme');
    if (score >= 4) return t('distress.labels.moderateToSevere');
    if (score >= 1) return t('distress.labels.mild');
    return t('distress.labels.none');
  };

  const stats = useMemo(() => {
    if (!combinedDistressLogs.length) return null;

    const scores = combinedDistressLogs
      .filter(log => typeof log.distressScore === 'number')
      .map(log => log.distressScore);

    if (!scores.length) return null;

    const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    const maxScore = Math.max(...scores);
    const minScore = Math.min(...scores);
    const highDistressCount = scores.filter(score => score >= 4).length;

    return {
      total: combinedDistressLogs.length,
      average: avgScore.toFixed(1),
      max: maxScore,
      min: minScore,
      highDistress: highDistressCount
    };
  }, [combinedDistressLogs]);

  if (loading) {
    return <LoadingSpinner text={t('distress.loading')} />;
  }

  if (error && !combinedDistressLogs.length) {
    return (
      <DistressContainer>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <h2>{t('distress.error.title')}</h2>
          <p>{error}</p>
          <RefreshButton onClick={fetchDistressLogs}>{t('distress.error.retry')}</RefreshButton>
        </div>
      </DistressContainer>
    );
  }

  return (
    <DistressContainer>
      <Header>
        <Title>{t('distress.title')}</Title>
        <Subtitle>{t('distress.subtitle')}</Subtitle>
      </Header>

      <DistressThermometer onSubmitted={fetchDistressLogs} />

      <StatusRow>
        <StatusGroup>
          <StatusPill status={!isOnline ? 'offline' : syncing ? 'syncing' : 'online'}>
            {!isOnline ? t('distress.status.offline') : syncing ? t('distress.status.syncing') : t('distress.status.online')}
          </StatusPill>
          {pendingDistressLogs.length > 0 && (
            <PendingBadge title={t('distress.status.pendingSyncEntries')}>
              ⏳ {pendingDistressLogs.length} {t('distress.status.pending')}
            </PendingBadge>
          )}
        </StatusGroup>
        {isOnline && pendingDistressLogs.length > 0 && (
          <SyncButton onClick={triggerSync} disabled={syncing}>
            {syncing ? t('distress.status.syncing') : t('distress.status.syncNow')}
          </SyncButton>
        )}
      </StatusRow>

      <ControlsSection>
        <FilterGroup>
          <FilterLabel>{t('distress.filters.minScore')}</FilterLabel>
          <Select
            value={filters.minScore}
            onChange={(e) => handleFilterChange('minScore', e.target.value)}
          >
            <option value="">{t('distress.filters.allScores')}</option>
            <option value="0">{t('distress.filters.score0')}</option>
            <option value="4">{t('distress.filters.score4')}</option>
            <option value="7">{t('distress.filters.score7')}</option>
          </Select>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>{t('distress.filters.startDate')}</FilterLabel>
          <DateInput
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            max={new Date().toISOString().split('T')[0]}
          />
        </FilterGroup>

        <FilterGroup>
          <FilterLabel>{t('distress.filters.endDate')}</FilterLabel>
          <DateInput
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            max={new Date().toISOString().split('T')[0]}
          />
        </FilterGroup>

        <RefreshButton onClick={() => setFilters({ minScore: '', startDate: '', endDate: '' })}>
          {t('distress.filters.clear')}
        </RefreshButton>
      </ControlsSection>

      {stats && (
        <StatsSection>
          <StatCard>
            <StatValue>{stats.total}</StatValue>
            <StatLabel>{t('distress.stats.totalEntries')}</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.average}</StatValue>
            <StatLabel>{t('distress.stats.averageScore')}</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.max}</StatValue>
            <StatLabel>{t('distress.stats.highestScore')}</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue style={{ color: stats.highDistress > 0 ? '#ef4444' : '#10b981' }}>
              {stats.highDistress}
            </StatValue>
            <StatLabel>{t('distress.stats.highDistress')}</StatLabel>
          </StatCard>
        </StatsSection>
      )}

      {combinedDistressLogs.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🌡️</EmptyIcon>
          <EmptyTitle>{t('distress.empty.title')}</EmptyTitle>
          <EmptyText>
            {filters.minScore || filters.startDate || filters.endDate
              ? t('distress.empty.textFilter')
              : t('distress.empty.textNoFilter')}
          </EmptyText>
        </EmptyState>
      ) : (
        <DistressList>
          <DistressHeader>
            <DistressTitle>{t('distress.header.title', { count: combinedDistressLogs.length })}</DistressTitle>
            <RefreshButton onClick={fetchDistressLogs}>{t('distress.header.refresh')}</RefreshButton>
          </DistressHeader>
          {combinedDistressLogs.map((log) => (
            <DistressItem key={log.id || log._id || log.localId}>
              <DistressItemHeader>
                <DistressScoreContainer>
                  <DistressScore>
                    <span style={{ fontWeight: 600, color: '#374151' }}>{t('distress.labels.distressScore')}</span>
                    <DistressBadge score={log.distressScore}>
                      {log.distressScore}/10
                    </DistressBadge>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      ({getDistressLabel(log.distressScore)})
                    </span>
                  </DistressScore>
                </DistressScoreContainer>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {log.isPending && <PendingLabel>{t('distress.status.pendingSync')}</PendingLabel>}
                  <DistressTime>
                    {format(new Date(log.timestamp || log.createdAt), 'MMM dd, yyyy • h:mm a')}
                  </DistressTime>
                </div>
              </DistressItemHeader>
              
              <DistressDetails>
                {log.distressSources && log.distressSources.length > 0 && (
                  <div>
                    <strong style={{ fontSize: '0.875rem', color: '#6b7280', display: 'block', marginBottom: '0.5rem' }}>
                      {t('distress.labels.sourcesOfDistress')}
                    </strong>
                    <DistressSources>
                      {log.distressSources.map((source, index) => {
                        // Check if source is a translation key (no spaces, matches a known key)
                        const i18nKey = `distressThermometer.sources.${source}`;
                        const i18nTranslation = t(i18nKey, { defaultValue: null });
                        const isTranslationKey = i18nTranslation && i18nTranslation !== i18nKey;
                        const emoji = problemEmojis[source] || '📋';
                        
                        if (isTranslationKey) {
                          // It's a translation key, use i18n
                          return (
                            <SourceTag key={index}>
                              <span style={{ marginRight: '0.35rem' }}>{emoji}</span>
                              {i18nTranslation}
                            </SourceTag>
                          );
                        } else {
                          // It's user-entered text, translate it
                          return (
                            <SourceTag key={index}>
                              <span style={{ marginRight: '0.35rem' }}>{emoji}</span>
                              <TranslatedText text={source} />
                            </SourceTag>
                          );
                        }
                      })}
                    </DistressSources>
                  </div>
                )}
                
                {log.distressNotes && (
                  <DistressNotes>
                    <strong style={{ display: 'block', marginBottom: '0.5rem', color: '#111827' }}>
                      {t('distress.labels.notes')}
                    </strong>
                    <TranslatedText text={log.distressNotes} />
                  </DistressNotes>
                )}
              </DistressDetails>

              {log.distressScore >= 4 && (
                <HighDistressAlert>
                  {t('distress.labels.alert')}
                </HighDistressAlert>
              )}
            </DistressItem>
          ))}
        </DistressList>
      )}
    </DistressContainer>
  );
};

export default Distress;

